import { useEffect, useId, useState, useRef } from "react";
import Select, {
  components,
  OptionProps,
  NoticeProps,
  SelectInstance,
  SingleValueProps,
} from "react-select";
import ClickAwayListener from "react-click-away-listener";
import { DropdownIcon, ButtonLoaderIcon, TickIcon } from "../../assets/icons";

type OptionType = {
  value: string;
  label: string;
  flag?: string; // Flag emoji is optional
};

type ComboboxProps = {
  options: Array<OptionType>;
  label?: string;
  error?: string;
  placeholder?: string;
  isLoading?: boolean;
  className?: string;
  showSelectedIndicator?: boolean;
  showEllipsis?: boolean;
  defaultValue?: OptionType;
  inputProps?: {
    name?: string;
    value?: OptionType;
    onBlur?: () => void;
    onMouseDown?: () => void;
    onInputChange?: (e: string) => void;
  };
  onChange?: (e: string) => void;
};

const LoadingIndicator = () => null;

const LoadingMessage = (props: NoticeProps<OptionType>) => (
  <components.LoadingMessage {...props}>
    <div className="cx-flex cx-items-center cx-justify-center cx-py-4">
      <ButtonLoaderIcon className="cx-animate-spin cx-w-5 cx-h-5 cx-text-brand-primary-regular" />
    </div>
  </components.LoadingMessage>
);

const Combobox = ({
  error,
  options,
  label,
  placeholder = "Select your option",
  isLoading = false,
  className = "",
  showSelectedIndicator = false,
  showEllipsis = true,
  defaultValue,
  inputProps = {},
  onChange,
}: ComboboxProps) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectRef = useRef<SelectInstance<OptionType>>(null);

  const SingleValueComponent = (props: SingleValueProps<OptionType>) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <div className="cx-flex cx-items-center cx-gap-3">
          {data.flag && <span>{data.flag}</span>}
          <span>{data.label}</span>
        </div>
      </components.SingleValue>
    );
  };

  const OptionComponent = (props: OptionProps<OptionType>) => {
    const {
      data,
      isSelected,
      innerProps: { className: optionClassName, ...restProps },
    } = props;

    return (
      <components.Option
        {...props}
        innerProps={{ ...restProps, className: `${optionClassName || ""}` }}
      >
        <div className="hover:cx-bg-primary hover:cx-text-text-inverse cx-cursor-pointer cx-py-2 cx-px-4 cx-flex cx-items-center cx-gap-3">
          {data.flag && (
            <div className="cx-text-2xl cx-mr-4 cx-mt-1">{data.flag}</div>
          )}
          <span
            className={`cx-flex-1 ${
              showEllipsis
                ? "cx-truncate"
                : "cx-whitespace-normal cx-break-words"
            }`}
          >
            {data.label}
          </span>
          {showSelectedIndicator && isSelected && (
            <TickIcon
              className="cx-ml-auto cx-w-4 cx-h-4"
              style={{ stroke: "#0A84FF" }}
            />
          )}
        </div>
      </components.Option>
    );
  };

  // Keep menu open while loading
  useEffect(() => {
    if (isLoading && !isOpen) {
      setIsOpen(true);
    }
  }, [isLoading, isOpen]);

  const handleInputChange = (inputValue: string) => {
    // Open the dropdown when typing
    if (inputValue.trim().length > 0) {
      setIsOpen(true);
    }
    // Trigger custom input change handler if provided
    if (inputProps.onInputChange) {
      inputProps.onInputChange(inputValue);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleControlMouseDown = () => {
    if (inputProps.onMouseDown) {
      inputProps.onMouseDown();
    }
  };

  const handleMenuOpen = () => {
    setIsOpen(true);
  };

  const handleMenuClose = () => {
    // Don't close menu while loading
    if (!isLoading) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <div
          className={`cx-relative cx-w-full cx-min-w-0 cx-rounded-base cx-border-2 cx-bg-background-default ${
            error
              ? "cx-border-stroke-error"
              : "cx-border-stroke-primary cx-focus-within:border-stroke-brand"
          } ${className}`}
          onMouseDown={handleControlMouseDown}
        >
          {label && (
            <label
              htmlFor={id}
              className={`cx-absolute cx-text-xs cx-text-text-tertiary cx-px-4 cx-pt-2 cx-top-0`}
            >
              {label}
            </label>
          )}
          <Select<OptionType>
            id={id}
            ref={selectRef}
            unstyled
            isMulti={false}
            options={options}
            menuIsOpen={isOpen}
            placeholder={placeholder}
            isLoading={isLoading}
            defaultValue={defaultValue}
            classNames={{
              container: () =>
                `cx-relative cx-bg-transparent cx-appearance-none cx-outline-none cx-min-w-0 ${
                  label ? "cx-pb-2 cx-pt-6" : "cx-pb-4 cx-pt-4"
                } cx-pl-4 cx-pr-10 cx-w-full cx-rounded-base`,
              menu: () =>
                "cx-bg-background-default cx--left-1 cx-border-2 cx-mt-1 cx-ml-1 cx-rounded cx-z-50",
              placeholder: () => "cx-text-text-tertiary cx-truncate",
              singleValue: () => "cx-truncate",
              input: () => "cx-min-w-0",
            }}
            components={{
              Option: OptionComponent,
              SingleValue: SingleValueComponent,
              LoadingIndicator,
              LoadingMessage,
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
            styles={{
              control: () => ({ minHeight: 0 }),
              menu: (styles) => ({ ...styles, zIndex: 100 }),
            }}
            {...inputProps}
            onFocus={handleFocus}
            onInputChange={handleInputChange} // Handle typing input
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onChange={(option) => {
              setIsOpen(false); // Close the dropdown when an option is selected
              if (onChange) {
                onChange(option?.value ?? "");
              }
            }}
          />
          <div className="cx-absolute cx-right-0 cx-top-0 cx-bottom-0 cx-py-4 cx-pr-4">
            <DropdownIcon height="100%" className={"cx-text-[#AAAAAA]"} />
          </div>
        </div>
      </ClickAwayListener>
      {error && (
        <div className="cx-text-xs cx-py-1 cx-text-text-error">{error}</div>
      )}
    </>
  );
};

export default Combobox;
